import React from 'react';
import type { BundleId } from '../../data/pricing';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
type Offer = {
  id: BundleId;
  badge: string;
  split: string;
  headline: string;
  body: string;
};
const OFFERS: Offer[] = [{
  id: 'sys8',
  badge: 'Most popular',
  split: '4 installed. 4 ready.',
  headline: 'Your complete weekly pool lighting system.',
  body: 'Designed for four permanent lighting positions and four charged backup lights. Enjoy your illuminated pool, exchange the set when charging is required, and recharge the removed units for the next rotation. Your installation locations stay fixed through every exchange.'
}, {
  id: 'sys24',
  badge: 'Best value',
  split: '12 installed. 12 ready.',
  headline: 'The complete lighting & backup system.',
  body: 'Built for twelve lighting positions with an additional twelve charged lights ready for easy rotation. Enjoy a larger illuminated space and the convenience of a full backup set.'
}];

/** Signature (primary) and Ultimate (premium) featured offers. */
export function FeaturedSystems() {
  const {
    lang
  } = useLanguage();
  const {
    startSystem
  } = useFunnel();
  const {
    setSelectedId
  } = useSystemSelection();
  const m = (n: number) => formatMoney(n, lang);
  return <section aria-labelledby="featured-title" className="border-t mf-hairline bg-deep" data-id="element-847" data-mp="ngdyiramrpe2">
      <div className="mx-auto max-w-site px-5 py-10 md:px-8 md:py-14" data-id="element-848" data-mp="ilrjme5cbep8">
        <h2 id="featured-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-849" data-mp="3r2ctry9pkdtx">
          One set installed. <span className="text-ice" data-id="element-850" data-mp="1q1g65cex7p9w">One set ready.</span>
        </h2>

        <div className="mt-6 grid gap-6 md:mt-8" data-id="element-851" data-mp="1pzc1gs976m8k">
          {OFFERS.map((o, i) => {
          const b = getBundle(o.id);
          const p = priceBundle(b);
          const primary = i === 0;
          return <article key={o.id} aria-labelledby={`offer-${o.id}`} className={`grid overflow-hidden border sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)] lg:grid-cols-[400px_minmax(0,1fr)] ${primary ? 'mf-hud border-marine bg-[#0D2440]' : 'border-white/15 bg-[#06121F]'}`} data-id="element-852" data-mp="23stj27ibpovy" data-mpi={i}>
                <img src={b.image} alt={`${b.name} — ${b.qty} M1 PRO lights`} loading="lazy" decoding="async" width={1122} height={1402} className="block aspect-[1122/1402] h-auto w-full self-start object-cover" data-id="element-853" data-mp="1g1xnr0ensbjy" />
                <div className="flex flex-col p-6 md:p-8 lg:p-10" data-id="element-854" data-mp="36lp12mtu5tto">
                  <p className={`text-xs font-semibold uppercase tracking-[0.16em] ${primary ? 'text-marine' : 'text-ice'}`} data-id="element-855" data-mp="31mcdfzv54iq1">{o.badge}</p>
                  <h3 id={`offer-${o.id}`} className="mt-2 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em]" data-id="element-856" data-mp="2wukn9qfwcf0f">
                    {b.tagline}
                  </h3>
                  <p className="mt-1 font-display text-lg uppercase tracking-[0.08em] text-titanium" data-id="element-857" data-mp="2nv7jneegeknm">
                    {b.qty} M1 PRO lights · <span className="text-white" data-id="element-858" data-mp="2iu9w0maocyeq">{o.split}</span>
                  </p>
                  <p className="mt-5 font-display text-xl font-semibold uppercase leading-tight text-white" data-id="element-859" data-mp="2dxjeduj81ur7">{o.headline}</p>
                  <p className="mt-3 text-[15px] leading-relaxed text-titanium" data-id="element-860" data-mp="29wzlj35oldav">{o.body}</p>

                  <div className="mt-auto pt-6" data-id="element-861" data-mp="c4ls6hjya0f8">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-862" data-mp="83a107lfh74x">
                      <span className="font-display text-5xl font-semibold leading-none" data-id="element-863" data-mp="f6zz06erlzo5">{m(p.total)}</span>
                      <span className="text-sm font-semibold uppercase tracking-[0.08em] text-ice" data-id="element-864" data-mp="k5ulbibxy6d0">Save {m(p.savings)}</span>
                    </div>
                    <p className="mt-1 text-sm text-titanium" data-id="element-865" data-mp="65s7c76r7x31">Only {m(p.perUnit)} per light</p>
                    <div className="mt-3" data-id="element-866" data-mp="a6c1d1w16kky">
                      <WarrantyTag tier={b.warranty} linked withShipping data-id="element-867" data-mp="3sm64bbjj2saf" />
                    </div>
                    <button type="button" onClick={() => {
                  track('featured_offer_click', {
                    bundleId: b.id,
                    value: p.total,
                    qty: b.qty
                  });
                  setSelectedId(b.id);
                  startSystem(b.id);
                }} className={`${buttonClasses(primary ? 'primary' : 'secondary', 'lg')} mt-5 w-full !whitespace-normal !px-4 text-center`} data-id="element-868" data-mp="16yp61givhwj">
                      Get my {b.tagline} — {m(p.total)}
                    </button>
                  </div>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}